import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Check, AlertCircle, Clock, Target } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface ProgressItem {
  id: string;
  label: string;
  completed: boolean;
  required: boolean;
}

interface ProgressSidebarProps {
  currentTab: string;
  profileData: any;
  onTabChange: (tab: string) => void;
}

export function ProgressSidebar({ currentTab, profileData, onTabChange }: ProgressSidebarProps) {
  // Define completion criteria for each section
  const getProgressItems = (): ProgressItem[] => [
    {
      id: 'dietary',
      label: 'Dietary preferences',
      completed: Boolean(profileData?.diet || profileData?.allergies?.length || profileData?.likes?.length),
      required: true,
    },
    {
      id: 'goals',
      label: 'Goals & macros',
      completed: Boolean(profileData?.goal || profileData?.macros?.calories !== 2000),
      required: false,
    },
    {
      id: 'household',
      label: 'Household setup',
      completed: Boolean(profileData?.householdSize > 1 || profileData?.equipment?.length),
      required: false,
    },
    {
      id: 'preferences',
      label: 'Food preferences',
      completed: Boolean(profileData?.favorites?.length || profileData?.heatLevel !== 2),
      required: false,
    },
    {
      id: 'notifications',
      label: 'Email settings',
      completed: Boolean(profileData?.weeklyEmail !== undefined),
      required: false,
    },
  ];

  const progressItems = getProgressItems();
  const completedItems = progressItems.filter(item => item.completed).length;
  const totalItems = progressItems.length;
  const requiredItems = progressItems.filter(item => item.required);
  const completedRequired = requiredItems.filter(item => item.completed).length;
  
  const overallProgress = (completedItems / totalItems) * 100;
  
  // Determine recommendation text
  const getRecommendation = () => {
    if (completedRequired < requiredItems.length) {
      return "Complete dietary preferences for better recommendations";
    }
    if (completedItems < 3) {
      return "Add more details for personalized meal plans";
    }
    if (completedItems < totalItems) {
      return "Complete your profile for the best experience";
    }
    return "Your profile is complete! 🎉";
  };

  return (
    <Card className="sticky top-6">
      <CardHeader>
        <CardTitle className="text-lg font-medium text-gray-900 font-header flex items-center space-x-2">
          <Target className="w-5 h-5 text-warning" />
          <span>Profile Completeness</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm text-gray-600">{completedItems}/{totalItems}</span>
          </div>
          <Progress 
            value={overallProgress} 
            className="h-3 bg-gray-200"
          />
          <p className="text-xs text-gray-600 mt-2">{Math.round(overallProgress)}% complete</p>
        </div>

        {/* Recommendation */}
        <div className="p-3 bg-coral/5 border border-coral/20 rounded-lg">
          <div className="flex items-start space-x-2">
            {completedItems === totalItems ? (
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Clock className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
            )}
            <p className="text-xs text-gray-700 leading-relaxed">
              {getRecommendation()}
            </p>
          </div>
        </div>

        {/* Section Checklist */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 font-header mb-3">Sections</h4>
          <div className="space-y-2">
            {progressItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={cn(
                  'w-full flex items-center space-x-3 p-2 rounded-md text-left transition-colors',
                  currentTab === item.id
                    ? 'bg-coral/10 text-coral border border-coral/20'
                    : 'desktop-hover:bg-gray-50 text-gray-700'
                )}
              >
                {/* Status icon */}
                <div className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0',
                  item.completed
                    ? 'bg-green-100 text-green-600'
                    : item.required
                      ? 'bg-coral/10 text-coral border border-coral'
                      : 'bg-gray-100 text-gray-400'
                )}>
                  {item.completed ? (
                    <Check className="w-3 h-3" />
                  ) : item.required ? (
                    <AlertCircle className="w-3 h-3" />
                  ) : (
                    <Clock className="w-3 h-3" />
                  )}
                </div>

                {/* Label */}
                <span className="text-sm flex-1 min-w-0">
                  {item.label}
                  {item.required && <span className="text-coral ml-1">*</span>}
                </span>

                {/* Status indicator */}
                {item.completed && (
                  <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Quick stats */}
        <div className="pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-gray-900 font-header">{completedRequired}/{requiredItems.length}</div>
              <div className="text-xs text-gray-600">Required</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900 font-header">{completedItems - completedRequired}/{totalItems - requiredItems.length}</div>
              <div className="text-xs text-gray-600">Optional</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}